<script setup lang="ts">
/**
 * AI视频创作平台 - 根组件
 */
import { onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { ConfigProvider, theme } from 'ant-design-vue';
import zhCN from 'ant-design-vue/es/locale/zh_CN';
import { useUserStore } from '@/stores/user';
import { useCreditsStore } from '@/stores/credits';
import MainLayout from '@/components/layout/MainLayout.vue';
import LoginModal from '@/components/auth/LoginModal.vue';

// 路由和用户状态
const route = useRoute();
const userStore = useUserStore();

// 是否隐藏布局（登录、注册、管理后台等页面）
const hideLayout = computed(() => {
  // 检查当前路由或匹配的路由是否设置了 hideLayout
  if (route.meta.hideLayout === true) return true;
  // 管理后台路由使用自己的布局
  if (route.path.startsWith('/admin')) return true;
  return false;
});

// 深色主题配置
const darkTheme = {
  algorithm: theme.darkAlgorithm,
  token: {
    // 主色调
    colorPrimary: '#0066FF',
    // 背景色
    colorBgContainer: '#1a1a1a',
    colorBgElevated: '#2a2a2a',
    colorBgLayout: '#0a0a0a',
    // 文字颜色
    colorText: '#FFFFFF',
    colorTextSecondary: '#CCCCCC',
    colorTextTertiary: '#999999',
    // 边框颜色
    colorBorder: '#333333',
    colorBorderSecondary: '#2a2a2a',
    // 圆角
    borderRadius: 8
  }
};

// 弹出层容器配置 - 统一挂到 body，避免被 overflow:hidden/auto 容器裁剪
const getPopupContainer = () => document.body;

// 应用启动时初始化用户状态
onMounted(async () => {
  await userStore.initUserState();
  
  // 已登录时，10 秒后再刷新一次积分（应对支付回调后积分延迟到账）
  if (userStore.isLoggedIn) {
    const creditsStore = useCreditsStore();
    setTimeout(() => {
      creditsStore.refreshBalance();
    }, 10000);
  }
});
</script>

<template>
  <ConfigProvider :locale="zhCN" :theme="darkTheme" :getPopupContainer="getPopupContainer">
    <!-- 需要布局的页面 -->
    <MainLayout v-if="!hideLayout">
      <router-view />
    </MainLayout>
    <!-- 不需要布局的页面（登录、注册等） -->
    <router-view v-else />
    
    <!-- 全局登录弹窗 -->
    <LoginModal />
  </ConfigProvider>
</template>

<style lang="scss">
/* 全局样式重置 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body,
#app {
  width: 100%;
  height: 100%;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    'Noto Color Emoji';
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: var(--bg-secondary);
}

::-webkit-scrollbar-thumb {
  background: var(--bg-tertiary);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #444444;
}
</style>
